import axios from 'axios';

// Default to empty string (relative URLs) for production - nginx proxies to backend
const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const CACHE_KEY_PREFIX = 'projects_cache_';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export interface Project {
  uuid: string;
  alias: string;
  description?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ProjectsResponse {
  projects: Project[];
  count: number;
}

class ProjectService {
  private static instance: ProjectService;
  private cache: Map<string, { data: ProjectsResponse; timestamp: number }> = new Map();

  private constructor() {
    // Load from localStorage per tenant
  }

  public static getInstance(): ProjectService {
    if (!ProjectService.instance) {
      ProjectService.instance = new ProjectService();
    }
    return ProjectService.instance;
  }

  /**
   * Get projects for a specific tenant
   * @param tenantId - The tenant ID to fetch projects for
   * @param forceRefresh - Force refresh from API
   */
  public async getProjects(tenantId: string, forceRefresh = false): Promise<ProjectsResponse> {
    const cacheKey = `${CACHE_KEY_PREFIX}${tenantId}`;

    // Return cached if valid and not forcing refresh
    const cached = this.cache.get(tenantId);
    if (!forceRefresh && cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    // Check localStorage cache
    if (!forceRefresh) {
      const localCached = localStorage.getItem(cacheKey);
      if (localCached) {
        const parsed = JSON.parse(localCached);
        if (Date.now() - parsed.timestamp < CACHE_DURATION) {
          this.cache.set(tenantId, parsed);
          return parsed.data;
        } else {
          localStorage.removeItem(cacheKey);
        }
      }
    }

    // Fetch from API
    // Note: Backend /api/projects endpoint will use default_tenant_id from JWT
    // We'll pass tenant_id as query param to ensure we get the right tenant's projects
    const response = await axios.get<ProjectsResponse>(
      `${API_BASE_URL}/api/projects`,
      {
        params: { tenant_id: tenantId }
      }
    );

    // Update cache
    const cacheData = {
      data: response.data,
      timestamp: Date.now(),
    };
    this.cache.set(tenantId, cacheData);
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));

    return response.data;
  }

  /**
   * Clear cache for a specific tenant or all tenants
   * @param tenantId - Optional tenant ID. If not provided, clears all caches.
   */
  public clearCache(tenantId?: string): void {
    if (tenantId) {
      this.cache.delete(tenantId);
      localStorage.removeItem(`${CACHE_KEY_PREFIX}${tenantId}`);
    } else {
      // Clear all project caches
      this.cache.clear();
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(CACHE_KEY_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    }
  }
}

export default ProjectService;
